import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import { IoEyeOutline } from "react-icons/io5";
import MasterShell from "../common/MasterShell";
import MasterListPage from "../common/MasterListPage";
import MasterToast from "../common/MasterToast";
import StatusBadge from "../common/StatusBadge";
import useRouteToast from "../common/useRouteToast";
import { getDistricts } from "../../services/districtService";
import { getSubDivisions } from "../../services/subDivisionService";
import {
  buildLookupMap,
  extractApiErrorMessage,
  formatMasterDate,
  matchesSearch,
} from "../../utils/masterHelpers";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import "./SubDivisions.css";

export default function SubDivisionList() {
  const navigate = useNavigate();
  const { clearToast, toast } = useRouteToast();
  const { canAny } = useCapabilities();
  const canCreateSubDivision = canAny("sub_divisions", ["create", "manage"]);
  const canReadSubDivision = canAny("sub_divisions", ["read", "manage"]);
  const canUpdateSubDivision = canAny("sub_divisions", ["update", "manage"]);
  const [districtLookup, setDistrictLookup] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [subDivisions, setSubDivisions] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [districts, subDivisionResponse] = await Promise.all([
        getDistricts(),
        getSubDivisions(),
      ]);

      setDistrictLookup(buildLookupMap(districts));
      setSubDivisions(Array.isArray(subDivisionResponse) ? subDivisionResponse : []);
    } catch (apiError) {
      setError(
        extractApiErrorMessage(apiError, "Failed to load sub-division data.")
      );
      setDistrictLookup({});
      setSubDivisions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = useMemo(
    () =>
      subDivisions.map((item) => ({
        ...item,
        district_name: districtLookup[item.district_id] || "-",
      })),
    [districtLookup, subDivisions]
  );

  const columns = [
    {
      key: "rowNumber",
      label: "Sr. No.",
      render: (_, __, rowNumber) => rowNumber,
    },
    { key: "code", label: "Code" },
    { key: "name", label: "Sub-division Name" },
    { key: "district_name", label: "District" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge value={row.is_active} />,
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row) => formatMasterDate(row.created_at),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="masters-table__actions">
          {canReadSubDivision ? (
            <button
              className="masters-icon-btn masters-icon-btn--view"
              onClick={() => navigate(`/sub-divisions/${row.id}/view`)}
              type="button"
            >
              <IoEyeOutline />
            </button>
          ) : null}
          {canUpdateSubDivision && (
            <button
              className="masters-icon-btn masters-icon-btn--edit"
              onClick={() => navigate(`/sub-divisions/${row.id}/edit`)}
              type="button"
            >
              <FiEdit />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <MasterShell pageClassName="sub-divisions-module">
      <MasterListPage
        addLabel={canCreateSubDivision ? "Add Sub-division" : undefined}
        addPath={canCreateSubDivision ? "/sub-divisions/add" : undefined}
        className="new_master-records-list"
        columns={columns}
        emptyMessage="Sub-division records will appear here after they are created."
        error={error}
        isLoading={loading}
        onRetry={fetchData}
        rows={rows}
        searchPlaceholder="Search by code, sub-division name, or district"
        searchPredicate={(row, searchTerm) =>
          matchesSearch([row.code, row.name, row.district_name], searchTerm)
        }
        title="Sub-divisions"
      />
      <MasterToast
        message={toast?.message || ""}
        onClose={clearToast}
        open={Boolean(toast)}
        severity={toast?.severity || "success"}
      />
    </MasterShell>
  );
}
